'use client';

import { LocationCard } from '@/components/blocks/LocationCard';
import { RegionSelect } from '@/components/inputs/RegionSelect';
import { ViewTabs } from '@/components/inputs/ViewTabs';
import {
  ALL_REGIONS,
  setLocationsView,
  setSelectedRegion,
  useLocationsView,
  useSelectedRegion,
  type LocationsView,
} from '@/lib/locations/store';
import { cn } from '@/lib/ui/cn';
import type { BlockLocationFinderProps } from '@/types/blocks';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';

export const LocationFinder = ({
  label,
  allOptionLabel,
  listTabLabel,
  mapTabLabel,
  regions,
  className,
  ...rest
}: BlockLocationFinderProps) => {
  const t = useTranslations('LocationFinder');
  const selected = useSelectedRegion();
  const view = useLocationsView();
  const tabsRef = useRef<HTMLDivElement>(null);

  const slugs = regions.map(r => r.slug).join(',');

  useEffect(() => {
    const applyHash = () => {
      const slug = window.location.hash.slice(1);
      const next = slug && slugs.split(',').includes(slug) ? slug : ALL_REGIONS;
      setSelectedRegion(next);
    };

    const handleHashChange = () => {
      applyHash();
      window.scrollTo({ top: 0 });
    };

    applyHash();
    if (window.location.hash) window.scrollTo({ top: 0 });
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [slugs]);

  useEffect(
    () => () => {
      setSelectedRegion(ALL_REGIONS);
      setLocationsView('list');
    },
    []
  );

  const handleChange = (value: string) => {
    setSelectedRegion(value);
    const { pathname, search } = window.location;
    window.history.replaceState(
      null,
      '',
      value === ALL_REGIONS
        ? `${pathname}${search}`
        : `${pathname}${search}#${value}`
    );
  };

  const handleView = (next: LocationsView) => {
    setLocationsView(next);
    if (next !== 'map' || !tabsRef.current) return;

    // Header is sticky and its height varies (promo banner), so measure it.
    const header = document.querySelector<HTMLElement>('[data-test="header"]');
    const top =
      tabsRef.current.getBoundingClientRect().top +
      window.scrollY -
      (header?.offsetHeight ?? 0);
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const options = [
    ...regions.map(region => ({ value: region.slug, label: region.label })),
    { value: ALL_REGIONS, label: allOptionLabel },
  ];

  const shown =
    selected === ALL_REGIONS
      ? regions
      : regions.filter(r => r.slug === selected);
  const count = shown.reduce((total, r) => total + (r.clinics?.length ?? 0), 0);

  return (
    <section
      data-component='location-finder'
      data-testid='location-finder'
      className={cn('w-full pt-8 md:pt-10', className)}
      {...rest}
    >
      <RegionSelect
        id='region-select'
        label={label}
        options={options}
        value={selected}
        onChange={handleChange}
      />

      {(listTabLabel || mapTabLabel) && (
        <ViewTabs
          ref={tabsRef}
          className='mt-4 lg:hidden'
          options={[
            { value: 'list', label: listTabLabel || t('listTab') },
            { value: 'map', label: mapTabLabel || t('mapTab') },
          ]}
          value={view}
          onChange={next => handleView(next as LocationsView)}
        />
      )}

      <p
        className={cn(
          'text-sh-dark-gray mt-6 font-sans text-sm leading-[130%] font-bold tracking-normal md:text-[21px]',
          view === 'map' && 'hidden lg:block'
        )}
        data-testid='location-finder-summary'
      >
        {selected === ALL_REGIONS
          ? t('showingAll', { count })
          : t('showingCount', { count, region: shown[0]?.label ?? '' })}
      </p>

      <ul
        className={cn(
          'mt-4 flex flex-col gap-4 lg:mt-6 lg:gap-6',
          view === 'map' && 'hidden lg:flex'
        )}
        data-testid='location-cards'
      >
        {shown.flatMap(region =>
          (region.clinics ?? []).map(clinic => (
            <li key={clinic.id}>
              <LocationCard {...clinic} />
            </li>
          ))
        )}
      </ul>
    </section>
  );
};
